import { z } from 'zod';
import { LineStringSchema, PointSchema } from './geojson';

/**
 * Road closure feature (Ice Storm Risk → "Road Closures") — served by
 * GET /api/road-closures. Backed directly by the R.1f `road_closures` table
 * (Ontario 511 + Alberta 511 via Castle-Rock REST; DriveBC via Open511). NO fusion
 * — this is a raw ingested operational signal (operator-cop-doctrine §4.5: the
 * ice-storm "downed trees/branches off roads" picture, and year-round construction/
 * incident closures otherwise).
 *
 * The AMBIENT register (operator-cop-doctrine §3B.8): a point marker is the click
 * target + anchor; where the upstream publishes a road geometry (the decoded
 * Castle-Rock polyline / DriveBC LineString) it renders as a ground-clamped line
 * beneath the dot. SHAPE encodes "a closure"; COLOUR encodes the closure-vs-
 * restriction register from the context-locked roadClosure palette; never pulses.
 *
 * Two shapes: the LIST endpoint returns the feature with the line geometry in
 * `segment` (or null); the DETAIL endpoint (`/:id`) adds the provenance tail
 * (source, source_record_id, ingested_at) for the panel footer.
 */

/** Normalized closure status vocabulary (mirrors the road_closures CHECK). */
export const ROAD_CLOSURE_STATUSES = ['active', 'planned', 'cleared', 'unknown'] as const;
export type RoadClosureStatus = (typeof ROAD_CLOSURE_STATUSES)[number];

/** Normalized severity vocabulary. */
export const ROAD_CLOSURE_SEVERITIES = ['minor', 'moderate', 'major', 'unknown'] as const;
export type RoadClosureSeverity = (typeof ROAD_CLOSURE_SEVERITIES)[number];

export const RoadClosurePropertiesSchema = z.object({
  id: z.string().uuid(),
  /** Province code: 'ON' | 'AB' | 'BC'. */
  province: z.string(),
  /** Roadway / highway name, when published. */
  roadway_name: z.string().nullable(),
  /** Direction of travel affected, free text. */
  direction: z.string().nullable(),
  /** Raw upstream event type (roadwork / incident / CONSTRUCTION / INCIDENT …). */
  event_type: z.string().nullable(),
  /** TRUE = full closure, FALSE = lane/partial restriction. The palette register. */
  is_closure: z.boolean(),
  /** Normalized severity — paired with the register label in the UI. */
  severity: z.string().nullable(),
  /** Short cause / event subtype, when published. */
  cause: z.string().nullable(),
  /** Upstream free-text description. */
  description: z.string().nullable(),
  /** When the closure started / was reported (UTC ISO), when published. */
  started_at: z.string().nullable(),
  /** Upstream planned/expected end time (UTC ISO), when published. */
  expected_end_at: z.string().nullable(),
  /** Normalized status — drives the active-only render default. */
  status: z.string(),
  /** Upstream snapshot last-updated time (UTC ISO). */
  observed_at: z.string(),
  /** True when this row carries an affected-segment line to render. */
  has_segment: z.boolean(),
});
export type RoadClosureProperties = z.infer<typeof RoadClosurePropertiesSchema>;

/**
 * The list-feature geometry is a Point (the representative location, always
 * present) — the click target + marker anchor. The optional affected-segment
 * LineString ships in `segment` so the layer can ground-clamp it beneath the dot
 * without a second fetch.
 */
export const RoadClosureFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  /** The affected road segment, when this row publishes one (else null). */
  segment: LineStringSchema.nullable(),
  properties: RoadClosurePropertiesSchema,
});
export type RoadClosureFeature = z.infer<typeof RoadClosureFeatureSchema>;

export const RoadClosureFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(RoadClosureFeatureSchema),
});
export type RoadClosureFeatureCollection = z.infer<typeof RoadClosureFeatureCollectionSchema>;

/** The detail endpoint adds the ingest provenance tail for the panel footer. */
export const RoadClosureDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  segment: LineStringSchema.nullable(),
  properties: RoadClosurePropertiesSchema.extend({
    /** Source/adapter tag ('511on' | '511ab' | 'drivebc'). */
    source: z.string(),
    /** The upstream event id (provenance). */
    source_record_id: z.string(),
    /** When WE wrote/last-refreshed the row (UTC ISO) — ingest provenance. */
    ingested_at: z.string(),
  }),
});
export type RoadClosureDetail = z.infer<typeof RoadClosureDetailSchema>;
